import { get } from "svelte/store";
import { bridge } from "../bridge/client";
import { goalSaveStatus, goalJournalPendingCount, journalReplaying } from "./state";
import {
  writeGoalJournal,
  readGoalJournal,
  clearGoalJournal,
  goalJournalPending,
} from "./goalJournal";
import { showcase } from "./nav";
import type { GoalPreferences } from "@contract/types";

/**
 * The single live-app entry point for a ring-goal edit. Gives goal saves the
 * same durability lap-label saves already have (labelJournal + liveCorrection),
 * by mirroring that idiom for a SINGLETON value: journal the intent BEFORE the
 * bridge write, verify by read-back, and drop the journal entry only once the
 * write is confirmed. Goals have no per-swim keying and can't interleave the way
 * labels do, so this is a much smaller engine than liveCorrection — no
 * single-flight coalescing queue, just newest-wins journaling with one retry.
 *
 * Fire-and-forget from the caller (void return): the sheet closes optimistically
 * and any stranded/failed edit surfaces through the shared save-trouble pill,
 * exactly like a stranded stroke fix.
 */

// Monotonic counter: each commitGoalChange claims the next id. Only the call
// whose id matches latestCommitId at resolution time may update goalSaveStatus.
let commitSeq = 0;
let latestCommitId = 0;
let inFlight = false;

/** Mirror the durable journal's size into the reactive store the pill reads. */
export function syncGoalJournalCount(): void {
  goalJournalPendingCount.set(goalJournalPending() ? 1 : 0);
}

/** Bare discriminant off BridgeUnavailableError's `.reason` (duck-typed), or
 *  `null` for a generic thrown error. Duck-typed (not `instanceof`) because
 *  tests swap `bridge` for a `vi.mock` factory that doesn't export the class.
 *  Kept local (not imported from liveCorrection) so the goals path stays
 *  self-contained. */
function bridgeReasonTag(err: unknown): "timeout" | "no-handler" | "rejected" | null {
  if (err && typeof err === "object" && "reason" in err) {
    const r = (err as { reason?: unknown }).reason;
    if (r === "timeout" || r === "no-handler" || r === "rejected") return r;
  }
  return null;
}

/** Order-independent identity of a goal's user-meaningful content. Ignores
 *  `updatedAt`, which native rewrites on every setGoals — comparing it would
 *  make every read-back look like a mismatch. */
function sameGoalIntent(a: GoalPreferences, b: GoalPreferences): boolean {
  return (
    (a.weekDistanceMeters ?? null) === (b.weekDistanceMeters ?? null) &&
    (a.peakHrBpm ?? null) === (b.peakHrBpm ?? null) &&
    (a.ageYears ?? null) === (b.ageYears ?? null)
  );
}

/** Discriminated read-back outcome (R2 readback-semantics precedent): a REJECTED
 *  getGoals after a RESOLVED setGoals is "unreadable" — the write already
 *  ack'd, we simply can't re-confirm it, so it counts as SAVED. A RESOLVED read
 *  whose intent differs is a genuine "mismatch" — keep it journaled and surface
 *  the failure. Collapsing the two would either nag on a healthy write or hide a
 *  real clobber. */
type ReadBack = { ok: true } | { ok: false; kind: "unreadable" | "mismatch" };

async function verifyGoalReadBack(committed: GoalPreferences): Promise<ReadBack> {
  let stored: GoalPreferences;
  try {
    stored = await bridge.getGoals();
  } catch {
    return { ok: false, kind: "unreadable" };
  }
  // Defensive: a read that resolved to a non-object can never confirm the
  // write (and must not throw out of sameGoalIntent's field reads).
  if (!stored || typeof stored !== "object") return { ok: false, kind: "mismatch" };
  return sameGoalIntent(committed, stored) ? { ok: true } : { ok: false, kind: "mismatch" };
}

/** One bridge attempt: write, then read back to confirm it landed. A verified
 *  write (or an unreadable-after-resolved-write) drops the journal entry and
 *  marks saved. Returns false only on a genuine write rejection or a resolved
 *  read-back mismatch — both of which keep the entry for a retry. */
async function attempt(entry: { commitId: number; prefs: GoalPreferences }): Promise<boolean> {
  try {
    await bridge.setGoals(entry.prefs);
  } catch (err) {
    // Reply-lost rescue (2026-07-09, mirrors liveCorrection.attempt): a
    // TIMED-OUT write may have LANDED — native persists setGoals to
    // UserDefaults synchronously BEFORE replying, so under contention only
    // the reply misses call()'s 5s race. A read-back that RESOLVES and
    // MATCHES the committed intent (updatedAt excluded — native rewrites it)
    // proves durability → saved, journal dropped, no pill. Timeout ONLY:
    // "rejected"/"no-handler" is a definitive refusal, and an unreadable or
    // MISMATCHED read here keeps the failure (after a FAILED write, no
    // verification means nothing is proven).
    if (bridgeReasonTag(err) === "timeout") {
      const rb = await verifyGoalReadBack(entry.prefs);
      if (rb.ok) {
        clearGoalJournal(entry.commitId);
        syncGoalJournalCount();
        if (entry.commitId === latestCommitId) goalSaveStatus.set("saved");
        return true;
      }
    }
    return false; // genuine bridge failure → keep journaled
  }
  const rb = await verifyGoalReadBack(entry.prefs);
  if (!rb.ok && rb.kind === "mismatch") {
    return false; // resolved read disagrees → genuine mismatch, keep + surface
  }
  // ok, OR unreadable-after-resolved-write → saved.
  clearGoalJournal(entry.commitId);
  syncGoalJournalCount();
  if (entry.commitId === latestCommitId) goalSaveStatus.set("saved");
  return true;
}

async function drain(): Promise<void> {
  inFlight = true;
  for (;;) {
    const entry = readGoalJournal();
    if (!entry) break;
    if (await attempt(entry)) continue; // cleared (or superseded newer entry) → loop
    // First attempt failed. Superseded meanwhile? Loop to pick up the newer edit.
    const superseding = readGoalJournal();
    if (superseding && superseding.commitId !== entry.commitId) continue;
    const retried = await attempt(entry);
    if (retried) continue;
    // Still failing and not superseded — leave it journaled for boot/nudge/pill retry.
    const after = readGoalJournal();
    if (after && after.commitId !== entry.commitId) continue;
    if (entry.commitId === latestCommitId) goalSaveStatus.set("failed");
    break;
  }
  inFlight = false;
}

export function commitGoalChange(next: GoalPreferences): void {
  const id = ++commitSeq;
  latestCommitId = id;
  goalSaveStatus.set("saving");

  // Durability: journal the intent BEFORE it touches the bridge. If the process
  // dies between here and a confirmed write, replayGoalJournal re-sends it on boot.
  writeGoalJournal({ commitId: id, prefs: next, ts: Date.now() });
  syncGoalJournalCount();

  if (!inFlight) void drain();
}

/**
 * Re-send a goal edit still sitting in the durable journal. Called on boot and
 * on the auth-resolved nudge, and by the save-trouble pill's retry. Reentrancy-
 * safe: an overlapping call shares the in-flight promise. Shares the
 * journalReplaying store with label replay so the pill's "Retrying…" feedback
 * covers both. Skipped on the public showcase page (never writes).
 */
let replayPromise: Promise<void> | null = null;

export function replayGoalJournal(): Promise<void> {
  if (replayPromise) return replayPromise;
  journalReplaying.set(true);
  replayPromise = doReplayGoalJournal().finally(() => {
    replayPromise = null;
    journalReplaying.set(false);
  });
  return replayPromise;
}

async function doReplayGoalJournal(): Promise<void> {
  if (get(showcase)) return; // public page never writes
  const entry = readGoalJournal();
  if (!entry) {
    syncGoalJournalCount();
    return;
  }
  // NATIVE-TRUTH PRE-READ (2026-07-09 escalation — the label journal's
  // stale-replay clobber has a singleton twin here). A goal entry stranded in
  // another session/origin can be OLDER than what native now holds; setGoals
  // is last-write-wins, so blind-replaying it would regress a newer edit.
  // Goals carry their own discriminator: native stamps updatedAt on every
  // setGoals. If native's stored goal was written strictly AFTER this entry
  // was journaled, the entry is stale — drain it without writing. If the
  // pre-read cannot be performed, write NOTHING (an uncomparable snapshot
  // must never clobber); the entry survives for the next boot/nudge.
  let stored: GoalPreferences | undefined;
  try {
    stored = await bridge.getGoals();
  } catch {
    syncGoalJournalCount();
    return; // leave journaled; retry next boot/nudge
  }
  if (stored && typeof stored === "object") {
    if (sameGoalIntent(entry.prefs, stored)) {
      clearGoalJournal(entry.commitId); // intent already on disk — nothing to recover
      syncGoalJournalCount();
      return;
    }
    const nativeUpdatedAtMs = stored.updatedAt ? Date.parse(stored.updatedAt) : NaN;
    if (Number.isFinite(nativeUpdatedAtMs) && nativeUpdatedAtMs > entry.ts) {
      clearGoalJournal(entry.commitId); // native is provably newer — never regress it
      syncGoalJournalCount();
      return;
    }
  }
  try {
    await bridge.setGoals(entry.prefs);
  } catch (err) {
    // Reply-lost rescue, replay flavor (2026-07-09 — see attempt() above):
    // launch contention can race out the reply of a replay write that native
    // persisted; without this the entry re-strands and re-pills on every
    // boot/nudge whose reply loses the race (the 7:28 AM boot-window pill).
    if (bridgeReasonTag(err) === "timeout") {
      const rb = await verifyGoalReadBack(entry.prefs);
      if (rb.ok) {
        clearGoalJournal(entry.commitId);
        syncGoalJournalCount();
        return;
      }
    }
    syncGoalJournalCount();
    return; // leave journaled; retry next boot/nudge
  }
  const rb = await verifyGoalReadBack(entry.prefs);
  if (!rb.ok && rb.kind === "mismatch") {
    syncGoalJournalCount();
    return; // unverified mismatch — retry later
  }
  clearGoalJournal(entry.commitId);
  syncGoalJournalCount();
}

/**
 * TEST-ONLY: reset every module-private binding to its first-load value so a
 * test starting fresh (or simulating a relaunch) cannot inherit a prior test's
 * in-flight write, replay latch, or commit counters. Mirrors
 * liveCorrection._resetForTests. Not referenced by production code.
 */
export function _resetGoalCorrectionForTests(): void {
  commitSeq = 0;
  latestCommitId = 0;
  inFlight = false;
  replayPromise = null;
}
